import {
  DEFAULT_INSPECTION_DATA_OPTIONS,
  DEFAULT_INSPECTION_LINK_TO_HUSTRO,
  DEFAULT_INSPECTION_REPORT_PARTS,
  DEFAULT_INSPECTION_SECTION_MASTERS,
  DEFAULT_INSPECTION_TEMPLATE_DATA,
  InspectionDataOptions,
  InspectionReportParts,
  InspectionTemplateData,
  SectionConfig,
} from 'shared/domain/report/types/model/common';
import { isImageDisplayStyle } from 'shared/domain/report/types/model/reportConfigGuards';
import { isPlainObject } from 'shared/utils/object';
import { normalizeBoolean } from 'shared/utils/value';
import type {
  SavedInspectionReportContentConfig,
  SavedInspectionReportFileTextMetadata,
} from './inspectionReportContentStorageTypes';

export type SavedInspectionReportContentConfigV1 = {
  parts: InspectionReportParts;
  templateData: InspectionTemplateData;
  inspectionData: InspectionDataOptions;
  linkToHustro: boolean;
  fileTextMetadata: SavedInspectionReportFileTextMetadata;
};

function isV1SavedConfig(
  raw: unknown
): raw is SavedInspectionReportContentConfigV1 {
  return (
    isPlainObject(raw) &&
    isPlainObject((raw as SavedInspectionReportContentConfigV1).parts) &&
    isPlainObject((raw as SavedInspectionReportContentConfigV1).templateData)
  );
}

function normalizeSectionConfigFromPartial(
  defaults: SectionConfig,
  partial: unknown
): SectionConfig {
  if (!isPlainObject(partial)) {
    return { ...defaults };
  }

  const saved = partial as Partial<SectionConfig>;
  return {
    enabled: normalizeBoolean(saved.enabled, defaults.enabled),
    displayStyle:
      saved.displayStyle !== undefined &&
      isImageDisplayStyle(saved.displayStyle)
        ? saved.displayStyle
        : defaults.displayStyle,
  };
}

/**
 * v1 → v2 field mapping (TDD generator v2).
 */
export function migrateInspectionReportContentV1ToV2(
  v1: SavedInspectionReportContentConfigV1
): SavedInspectionReportContentConfig {
  const parts = v1.parts ?? DEFAULT_INSPECTION_REPORT_PARTS;
  const templateData = v1.templateData ?? DEFAULT_INSPECTION_TEMPLATE_DATA;
  const inspectionData = v1.inspectionData ?? DEFAULT_INSPECTION_DATA_OPTIONS;

  const templateSectionEnabled =
    templateData.generalStandard ||
    templateData.generalStandardAttachments.enabled;

  return {
    ...DEFAULT_INSPECTION_SECTION_MASTERS,
    templateSectionEnabled,
    checklistSectionEnabled: parts.checklist,
    issueDataSectionEnabled: parts.issueRegister,
    inspectionInfo: normalizeBoolean(parts.inspectionInfo, true),
    summary: parts.summary,
    signatures: parts.signatures,
    generalStandard: templateData.generalStandard,
    generalStandardAttachments: normalizeSectionConfigFromPartial(
      DEFAULT_INSPECTION_TEMPLATE_DATA.generalStandardAttachments,
      templateData.generalStandardAttachments
    ),
    sectionDescriptions: templateData.sectionDescriptions,
    sectionAttachments: normalizeSectionConfigFromPartial(
      DEFAULT_INSPECTION_TEMPLATE_DATA.sectionAttachments,
      templateData.sectionAttachments
    ),
    controlledElementStandard: templateData.controlledElementStandard,
    controlledElementStandardAttachments: normalizeSectionConfigFromPartial(
      DEFAULT_INSPECTION_TEMPLATE_DATA.controlledElementStandardAttachments,
      templateData.controlledElementStandardAttachments
    ),
    controlledElementAttachments: normalizeSectionConfigFromPartial(
      DEFAULT_INSPECTION_DATA_OPTIONS.controlledElementAttachments,
      inspectionData.controlledElementAttachments
    ),
    showNotApplicable: inspectionData.showNotApplicable,
    showUnchecked: inspectionData.showUnchecked,
    linkToHustro: normalizeBoolean(
      v1.linkToHustro,
      DEFAULT_INSPECTION_LINK_TO_HUSTRO
    ),
    fileTextMetadata: v1.fileTextMetadata ?? {
      showFileNames: false,
      showFileDescriptions: false,
      showActiveLinks: false,
    },
  };
}

export function tryMigrateRawInspectionContentConfig(
  raw: unknown
): SavedInspectionReportContentConfig | null {
  if (!isV1SavedConfig(raw)) {
    return null;
  }

  if (
    'templateSectionEnabled' in raw &&
    typeof (raw as { templateSectionEnabled?: unknown }).templateSectionEnabled ===
      'boolean'
  ) {
    return null;
  }

  return migrateInspectionReportContentV1ToV2(raw);
}
